/**
 * The agent's answers, rendered.
 *
 * The model is told it is writing into a markdown surface, so it writes
 * `**45.500 BHD**` and pipe tables. Showing that raw — which is what a
 * `whitespace-pre-wrap` div does — puts asterisks around the one number in the
 * sentence that mattered.
 *
 * GFM is on for tables and strikethrough. Tables are the reason: an agent
 * summarising six overdue invoices produces a table, and a table rendered as
 * pipes is unreadable.
 *
 * WHAT IS DELIBERATELY NOT ALLOWED
 *
 * No raw HTML (`rehype-raw` is absent, and react-markdown escapes HTML by
 * default). This text is written by a model that has just read a customer's
 * uploaded spreadsheet — a file could contain `<img src=x onerror=…>` and the
 * model could repeat it. Escaping is not a formatting preference here, it is
 * the boundary between "the agent read your file" and "your file ran code in
 * your dashboard".
 *
 * Links open in a new tab with `noreferrer`, for the same reason.
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

export function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div
      dir="auto"
      className={cn(
        'text-[13.5px] leading-relaxed',
        // Spacing is set per element rather than with `space-y`, so a reply
        // that is one paragraph has no stray margin and a reply with a list
        // still breathes.
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        '[&_p]:my-2',
        '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:ps-5',
        '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:ps-5',
        '[&_li]:my-0.5',
        '[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-[15px] [&_h1]:font-semibold',
        '[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-[14px] [&_h2]:font-semibold',
        '[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-[13.5px] [&_h3]:font-semibold',
        '[&_strong]:font-semibold [&_strong]:text-foreground',
        '[&_a]:underline [&_a]:underline-offset-2',
        '[&_blockquote]:border-s-2 [&_blockquote]:ps-3 [&_blockquote]:text-muted-foreground',
        '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[12px]',
        '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-muted [&_pre]:p-3',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
        '[&_hr]:my-3 [&_hr]:border-border',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ ...props }) => <a {...props} target="_blank" rel="noreferrer noopener" />,
          /*
            A table wide enough to need scrolling must scroll INSIDE itself. A
            markdown table in a 23rem rail routinely exceeds it, and without
            this wrapper the whole panel scrolls sideways instead.
          */
          table: ({ ...props }) => (
            <div className="my-2 overflow-x-auto">
              <table className="w-full border-collapse text-[12.5px]" {...props} />
            </div>
          ),
          th: ({ ...props }) => (
            <th
              className="border-b border-border px-2 py-1.5 text-start font-medium text-muted-foreground"
              {...props}
            />
          ),
          td: ({ ...props }) => (
            <td className="border-b border-border/60 px-2 py-1.5 align-top" {...props} />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
